/**
 * BUNDLE BUILDER  —  assets/fk-bundle-builder.js
 *
 * Scoped custom element. Deliberately isolated from theme.js:
 *   - does NOT use ProductVariants (its selectVariant() dispatches variant:changed)
 *   - does NOT dispatch variant:changed (global handlers in custom.js would
 *     scroll-to-top and rebuild the Rebuy featured-products block on every click)
 *   - does NOT touch the variantState singleton
 *   - does NOT read/write the unscoped sessionStorage keys
 *     (variantHandle / variantSelectedId)
 *   - all DOM queries scoped to `this`, except the price mount point
 *
 * RENDERING CONTRACT: the component rows and swatches are rendered in Liquid by
 * snippets/fk-bundle-builder.liquid. This file never builds them. It reads the
 * initial selection back off the DOM, then does targeted updates on click, so
 * focus and the open/closed state of the accordions are preserved.
 *
 * Accordion open/close is owned entirely by the theme's <collapsible-content>
 * and is="toggle-button" elements. Nothing here touches it.
 *
 * Pricing: per-slot fixed prices from custom.bundle_prices (config.bundlePrices,
 * cents, same order as components). The cart transform applies the same values
 * as fixedPricePerUnit, so the PDP and the cart agree exactly.
 *
 * ATC contract: the shell variant is added with `_bundle_variant_ids`, a
 * comma-separated list of the selected component variant IDs in component
 * order. The cart transform expands the line from that property.
 */
class BundleBuilder extends HTMLElement {
  connectedCallback() {
    const configEl = this.querySelector("[data-bb-config]");
    if (!configEl) return;

    try {
      this.config = JSON.parse(configEl.textContent);
    } catch (e) {
      console.error("[bundle-builder] bad config JSON", e);
      return;
    }

    this.componentsEl = this.querySelector("[data-bb-components]");
    this.atcButton = this.querySelector("[data-bb-atc]");
    this.statusEl = this.querySelector("[data-bb-status]");
    if (!this.componentsEl || !this.atcButton) return;

    // component index -> pattern key
    this.selections = new Map();

    this._readSelectionsFromDom();
    this.pricing = this._slotPricing();
    this._renderPrice();
    this.config.components.forEach((c) => this._renderComponentPrice(c));
    this._updateAtc();

    // Delegated: one listener for every swatch and details link in every
    // component, so nothing needs rebinding when a selection changes.
    this.componentsEl.addEventListener("click", (e) => {
      const row = e.target.closest("[data-bb-component]");
      if (!row || !this.componentsEl.contains(row)) return;

      const swatch = e.target.closest("[data-bb-swatch]");
      if (swatch) {
        this._select(Number(row.dataset.bbComponent), swatch.dataset.bbSwatch);
        return;
      }

      if (e.target.closest("[data-bb-details]")) {
        this._openDetails(Number(row.dataset.bbComponent));
      }
    });

    this._setupModal();

    this.componentsEl.addEventListener("keydown", (e) => {
      const group = e.target.closest(".bb__swatches");
      if (group) this._onSwatchKeydown(e, group);
    });

    this.atcButton.addEventListener("click", () => this._addToCart());
  }

  /* ---------------------------------------------------------------- state */

  /**
   * Liquid decides what is preselected (the first pattern it emits, which is
   * the anchor product). Read that back rather than re-deriving it here, so
   * there is exactly one source of truth.
   */
  _readSelectionsFromDom() {
    this.config.components.forEach((c) => {
      const row = this.componentsEl.querySelector(`[data-bb-component="${c.index}"]`);
      const pressed = row && row.querySelector('[data-bb-swatch][aria-pressed="true"]');

      if (pressed) {
        this.selections.set(c.index, pressed.dataset.bbSwatch);
        return;
      }

      // Defensive: markup without a pressed swatch still yields a usable state.
      const first = c.patterns.find((p) => p.available);
      if (first) this.selections.set(c.index, first.key);
    });
  }

  _patternFor(component) {
    const key = this.selections.get(component.index);
    return component.patterns.find((p) => p.key === key) || null;
  }

  /* -------------------------------------------------------------- pricing */

  /**
   * Per-slot fixed prices, matched by position.
   * `original` is the selected pattern's real price; `final` is the slot price.
   */
  _slotPricing() {
    const prices = this.config.bundlePrices || [];
    const components = this.config.components.map((c, i) => {
      const p = this._patternFor(c);
      const original = p ? p.price : c.price;
      const final = Number.isFinite(prices[i]) ? prices[i] : original;
      return { original, final, discount: Math.max(0, original - final) };
    });
    const subtotal = components.reduce((a, c) => a + c.original, 0);
    const total = components.reduce((a, c) => a + c.final, 0);
    return { subtotal, total, totalOff: subtotal - total, components };
  }

  /* --------------------------------------------------------------- render */

  _renderComponentPrice(component) {
    const el = this.componentsEl.querySelector(
      `[data-bb-component="${component.index}"] [data-bb-component-price]`
    );
    if (!el) return;
    const line = this.pricing.components[component.index];
    el.innerHTML = "";

    // Classes come from the markup (fk-bundle-builder.liquid), not from here —
    // this rebuild replaces the server-rendered seed on every swatch change.
    if (line.discount > 0) {
      const was = document.createElement("span");
      was.className = el.dataset.bbCompareClass || "price price--compare";
      was.textContent = this._money(line.original);
      el.append(was, " ");
    }

    const now = document.createElement("span");
    now.className = el.dataset.bbPriceClass || "price";
    now.textContent = this._money(line.final);
    el.append(now);
  }

  /**
   * Title-block price: "$97 ($123 value)". Mount lives in fk-bundle-info.liquid.
   *
   * This replaces the server-rendered seed, so the price span's classes are
   * read from data-bb-price-class rather than hardcoded here — otherwise
   * editing the seed markup appears to do nothing.
   */
  _renderPrice() {
    const el = document.querySelector("[data-bb-price]");
    if (!el) return;
    el.innerHTML = "";

    const now = document.createElement("span");
    now.className = el.dataset.bbPriceClass || "price";
    now.textContent = this._money(this.pricing.total);
    el.append(now);

    if (this.pricing.totalOff > 0) {
      const value = document.createElement("span");
      value.className = "bb__price-value";
      value.textContent = `(${this._money(this.pricing.subtotal)} value)`;
      el.append(value);
    }
  }

  /* ------------------------------------------------------------ selection */

  _onSwatchKeydown(event, container) {
    const keys = ["ArrowRight", "ArrowLeft", "ArrowUp", "ArrowDown", "Home", "End"];
    if (!keys.includes(event.key)) return;
    event.preventDefault();

    const items = Array.from(container.querySelectorAll(".bb__swatch:not([disabled])"));
    if (!items.length) return;

    const current = items.indexOf(document.activeElement);
    let next;
    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        next = (current + 1) % items.length;
        break;
      case "ArrowLeft":
      case "ArrowUp":
        next = (current - 1 + items.length) % items.length;
        break;
      case "Home":
        next = 0;
        break;
      case "End":
        next = items.length - 1;
        break;
    }
    items[next].focus();
  }

  _select(componentIndex, patternKey) {
    if (this.selections.get(componentIndex) === patternKey) return;

    const component = this.config.components.find((c) => c.index === componentIndex);
    if (!component) return;

    this.selections.set(componentIndex, patternKey);

    const pattern = this._patternFor(component);
    const row = this.componentsEl.querySelector(`[data-bb-component="${componentIndex}"]`);
    if (!row || !pattern) return;

    // Targeted updates — no re-render, so focus and accordion state survive.
    const img = row.querySelector("[data-bb-component-image]");
    if (img && pattern.image) img.src = pattern.image;

    const name = row.querySelector("[data-bb-pattern-name]");
    if (name) name.textContent = pattern.label;

    row.querySelectorAll("[data-bb-swatch]").forEach((sw) => {
      const on = sw.dataset.bbSwatch === patternKey;
      sw.setAttribute("aria-pressed", on ? "true" : "false");
      sw.tabIndex = on ? 0 : -1; // roving tabindex
    });

    // Component's real price may differ between patterns — recompute.
    this.pricing = this._slotPricing();
    this._renderPrice();
    this.config.components.forEach((c) => this._renderComponentPrice(c));

    // Scoped event only. Never `variant:changed`.
    this.dispatchEvent(
      new CustomEvent("bundle:component-changed", {
        bubbles: true,
        detail: { componentIndex, pattern },
      })
    );

    this._updateAtc();
  }

  _updateAtc() {
    const complete = this.config.components.every((c) => {
      const p = this._patternFor(c);
      return p && p.available;
    });

    this.atcButton.disabled = !complete;

    // Queried fresh: LoaderButton rewrites the button's innerHTML when it
    // upgrades, which detaches any node cached before that ran.
    const textEl = this.atcButton.querySelector("[data-bb-atc-text]");
    if (!textEl) return;

    if (!complete) {
      textEl.textContent = "Select a pattern for each item";
      return;
    }

    const count = this.config.components.length;
    textEl.textContent = `Add to Bag (${count}) • ${this._money(this.pricing.total)}`;

    // The "value" half is a separate element so it can be de-emphasised;
    // the rest of the label stays a plain text node.
    if (this.pricing.totalOff > 0) {
      const value = document.createElement("span");
      value.className = "bb__atc-value";
      value.textContent = ` (${this._money(this.pricing.subtotal)} value)`;
      textEl.append(value);
    }
  }

  /* ---------------------------------------------------------- details modal */

  _setupModal() {
    this.modal = this.querySelector("[data-bb-modal]");
    if (!this.modal || typeof this.modal.showModal !== "function") return;

    this.modalContent = this.modal.querySelector("[data-bb-modal-content]");
    this.detailsCache = new Map(); // product handle -> fragment HTML

    this.modal
      .querySelector("[data-bb-modal-close]")
      ?.addEventListener("click", () => this.modal.close());

    // Clicking the backdrop closes. The backdrop is the dialog element itself
    // (its ::backdrop is not an event target), so only treat clicks that land
    // outside the content box as a dismiss.
    this.modal.addEventListener("click", (e) => {
      if (e.target !== this.modal) return;
      const box = this.modal.getBoundingClientRect();
      const outside =
        e.clientX < box.left ||
        e.clientX > box.right ||
        e.clientY < box.top ||
        e.clientY > box.bottom;
      if (outside) this.modal.close();
    });
  }

  /**
   * Opens the shared dialog for whichever pattern is currently selected on this
   * component. Descriptions are far too big to ship in the config JSON, so the
   * fragment is fetched on demand and cached per handle for the page's life.
   */
  async _openDetails(componentIndex) {
    if (!this.modal) return;

    const component = this.config.components.find((c) => c.index === componentIndex);
    const pattern = component && this._patternFor(component);
    if (!pattern || !pattern.productHandle) return;

    const handle = pattern.productHandle;
    this.modal.showModal();

    const cached = this.detailsCache.get(handle);
    if (cached !== undefined) {
      this.modalContent.innerHTML = cached;
      return;
    }

    this.modalContent.innerHTML = '<p class="bb__modal-loading">Loading…</p>';
    // Guards against a slow response landing after the shopper has moved on.
    this.modalRequest = handle;

    try {
      const res = await fetch(`/products/${encodeURIComponent(handle)}?view=fk-bundle-modal`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const html = await res.text();
      this.detailsCache.set(handle, html);
      if (this.modalRequest === handle) this.modalContent.innerHTML = html;
    } catch (e) {
      console.error("[bundle-builder] could not load product details:", e);
      if (this.modalRequest === handle) {
        this.modalContent.innerHTML =
          '<p class="bb__modal-loading">Sorry — we couldn’t load these details.</p>';
      }
    }
  }

  /* ------------------------------------------------------------------ atc */

  /**
   * TODO (pre-production): re-validate availability here before adding.
   * Rendered inventory is baked into a cached page and goes stale.
   * Suggested: a `?view=bundle-availability` alternate template returning JSON,
   * matching the existing `?view=product-item-CTS` pattern.
   */
  async _addToCart() {
    this.atcButton.disabled = true;
    this.atcButton.setAttribute("aria-busy", "true"); // drives the theme spinner

    const properties = {};
    const variantIds = [];
    const missing = [];

    this.config.components.forEach((c) => {
      const p = this._patternFor(c);
      if (!p) {
        missing.push(c.title);
        return;
      }
      variantIds.push(p.variantId);
    });

    if (missing.length) {
      console.warn("[bundle-builder] components with no selection:", missing);
      this._status("Select a pattern for each item.", "error");
      this.atcButton.removeAttribute("aria-busy");
      this.atcButton.disabled = false;
      return;
    }

    properties._bundle_variant_ids = variantIds.join(",");

    const payload = {
      items: [{ id: this.config.bundleVariantId, quantity: 1, properties }],
    };

    try {
      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify(payload),
      });

      const raw = await res.text();
      let data;
      try {
        data = JSON.parse(raw);
      } catch (e) {
        console.error("[bundle-builder] non-JSON cart response:", raw.slice(0, 500));
        this._status("Unexpected response from cart.", "error");
        return;
      }

      if (!res.ok) {
        console.error("[bundle-builder] cart/add failed:", data);
        this._status(data.description || data.message || "Could not add to bag.", "error");
        return;
      }

      const cart = await (await fetch("/cart.js")).json();

      const line = cart.items.find(
        (i) =>
          i.properties &&
          i.properties._bundle_variant_ids === properties._bundle_variant_ids
      );

      // Production canary: the cart transform should charge exactly what the
      // PDP quoted. If these diverge the fixedPricePerUnit values are stale.
      if (line && line.final_price !== this.pricing.total) {
        console.warn(
          `[bundle-builder] PDP/cart price mismatch — PDP shows ${this.pricing.total}, cart charges ${line.final_price} per unit.`
        );
      }

      document.documentElement.dispatchEvent(
        new CustomEvent("cart:updated", { bubbles: true, detail: { cart } })
      );
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:refresh", {
          bubbles: true,
          detail: { cart, openMiniCart: true },
        })
      );
    } catch (e) {
      console.error("[bundle-builder] ATC threw:", e);
      this._status("Something went wrong. Please try again.", "error");
    } finally {
      this.atcButton.removeAttribute("aria-busy");
      this.atcButton.disabled = false;
      this._updateAtc();
    }
  }

  /* --------------------------------------------------------------- helpers */

  /**
   * Errors only. The "Adding…" / "Added to bag." progress messages were
   * removed deliberately — the theme's own cart drawer already confirms the
   * add. Failures still surface here, otherwise a failed add is silent.
   */
  _status(message, state) {
    if (!this.statusEl) return;
    this.statusEl.textContent = message;
    if (state) {
      this.statusEl.dataset.state = state;
    } else {
      delete this.statusEl.dataset.state;
    }
  }

  /** Kept in lockstep with snippets/fk-bundle-money.liquid. */
  _money(cents) {
    const value = cents / 100;
    return `$${Number.isInteger(value) ? value : value.toFixed(2)}`;
  }
}

if (!window.customElements.get("bundle-builder")) {
  window.customElements.define("bundle-builder", BundleBuilder);
}
